import React, { useState, useCallback, useContext } from 'react';
import { Button, CircularProgress, Box } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
// Import the useExport hook
import { useExport } from '../../context/ExportContext';
import { FormContext } from '../../context/FormContext';
import { PaginationContext } from '../../context/PaginationContext';
import { useLanguage } from '../../localization';
import PaymentModal from '../Payment/PaymentModal';
import stripePromise from '../../config/stripe';


// Removed unused context imports

const ExportButton = React.memo(({
  variant = "contained",
  color = "primary",
  selectedSections = [], // Add selectedSections prop
  setSelectedSections, // Add setSelectedSections prop
  sx // Allow passing sx prop
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  // Get the export function from the context
  const { initiatePdfExport } = useExport();
  const { formData } = useContext(FormContext);
  const { pages } = useContext(PaginationContext);
  const { t } = useLanguage();

  // Validation function to check if PDF content exists
  const validatePdfContent = useCallback(() => {
    // Same validation logic as in ExportContext
    if (!pages || pages.length === 0) {
      console.warn("PDF Export validation failed: No pages calculated.");
      alert("Cannot export PDF: No content pages have been generated.");
      return false;
    }

    if (!formData) {
      console.warn("PDF Export validation failed: Form data is not available.");
      alert("Cannot export PDF: Resume data is missing.");
      return false;
    }

    return true;
  }, [pages, formData]);

  // Handle successful payment - proceed with PDF export
  const handlePaymentSuccess = useCallback(async () => {
    setIsExporting(true);
    try {
      // Call the export function from the context
      await initiatePdfExport();
    } catch (error) {
      // Context and service handle errors, but maybe log here?
      console.error("ExportButton encountered an error:", error);
    }
    finally {
      setIsExporting(false);
    }
  }, [initiatePdfExport]);



  // Handle the initial button click - show payment modal or proceed directly
  const handleExportPDF = useCallback(async () => {
    if (isExporting) {
      return;
    }
    
    // Validate PDF content before proceeding with payment or export
    if (!validatePdfContent()) {
      return; // Stop here if validation fails
    }
    
    // If Stripe is not configured, proceed directly with PDF export
    if (!stripePromise) {
      console.warn('Stripe not configured - proceeding with direct PDF export');
      setIsExporting(true);
      try {
        await initiatePdfExport();
      } catch (error) {
        console.error("ExportButton encountered an error:", error);
      } finally {
        setIsExporting(false);
      }
      return;
    }
    
    // Only show payment modal if validation passed
    setShowPaymentModal(true);
  }, [isExporting, initiatePdfExport, validatePdfContent]);

  // Handle payment modal close
  const handlePaymentClose = useCallback(() => {
    setShowPaymentModal(false);
  }, []);

  return (
    <>
    <Button
      id="export-pdf-button"
      data-umami-event="Download PDF Button"
      variant={variant}
      color={color}
        startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <PictureAsPdfIcon />
          </Box>
        )}
      onClick={handleExportPDF}
      disabled={isExporting} // Disable only based on local exporting state
      sx={{ whiteSpace: 'nowrap', ...sx }} // Combine passed sx with defaults
    >
        {isExporting ? t('header.exporting') : (stripePromise ? t('header.exportPdf') : 'PDF - Free')}
    </Button>

      <PaymentModal
        open={showPaymentModal}
        onClose={handlePaymentClose}
        onSuccess={handlePaymentSuccess}
        stripePromise={stripePromise}
        selectedSections={selectedSections}
      />
    </>
  );
});

export default ExportButton; 